/** Excel 2003 SpreadsheetML (.xls) without extra packages. */

export type ExcelCell =
  | {
      type: 'string';
      value: string;
      style?: string;
      mergeAcross?: number;
    }
  | { type: 'number'; value: number; style?: string }
  | { type: 'date'; value: string; style?: string }
  | { type: 'formula'; formula: string; value?: number; style?: string }
  | { type: 'empty'; style?: string; mergeAcross?: number };

type SpreadsheetOptions = {
  sheetName: string;
  columnWidths: number[];
  rows: ExcelCell[][];
  freezeRows?: number;
  autoFilter?: { headerRow: number; lastDataRow: number; columns: number };
  headerLeft?: string;
  footerLeft?: string;
  footerRight?: string;
};

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sheetName(name: string): string {
  const cleaned = name.replace(/[\\/*?[\]:]/g, ' ').trim();
  return (cleaned || 'Sheet1').slice(0, 31);
}

function renderCell(cell: ExcelCell): string {
  const style = 'style' in cell && cell.style ? ` ss:StyleID="${cell.style}"` : '';
  const merge =
    'mergeAcross' in cell && cell.mergeAcross
      ? ` ss:MergeAcross="${cell.mergeAcross}"`
      : '';

  if (cell.type === 'empty') {
    return `<Cell${style}${merge}/>`;
  }
  if (cell.type === 'string') {
    return `<Cell${style}${merge}><Data ss:Type="String">${xmlEscape(cell.value)}</Data></Cell>`;
  }
  if (cell.type === 'number') {
    return `<Cell${style}${merge}><Data ss:Type="Number">${cell.value}</Data></Cell>`;
  }
  if (cell.type === 'date') {
    return `<Cell${style}${merge}><Data ss:Type="DateTime">${xmlEscape(cell.value)}</Data></Cell>`;
  }
  const cached =
    cell.value != null
      ? `<Data ss:Type="Number">${cell.value}</Data>`
      : '';
  return `<Cell${style}${merge} ss:Formula="${xmlEscape(cell.formula)}">${cached}</Cell>`;
}

function renderRow(cells: ExcelCell[], height?: number): string {
  const heightAttr = height != null ? ` ss:Height="${height}"` : '';
  return `<Row${heightAttr}>${cells.map(renderCell).join('')}</Row>`;
}

export function isoDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? `${match[1]}T00:00:00.000` : null;
}

export function downloadSpreadsheetMl(
  filename: string,
  options: SpreadsheetOptions,
): void {
  const columns = options.columnWidths
    .map((width) => `<Column ss:Width="${width}"/>`)
    .join('');

  const rows = options.rows.map((row) => renderRow(row)).join('');

  const freeze =
    options.freezeRows && options.freezeRows > 0
      ? `<FreezePanes/>
   <FrozenNoSplit/>
   <SplitHorizontal>${options.freezeRows}</SplitHorizontal>
   <TopRowBottomPane>${options.freezeRows}</TopRowBottomPane>
   <ActivePane>2</ActivePane>`
      : '';

  const autoFilter = options.autoFilter
    ? `<AutoFilter x:Range="R${options.autoFilter.headerRow}C1:R${options.autoFilter.lastDataRow}C${options.autoFilter.columns}" xmlns="urn:schemas-microsoft-com:office:excel"/>`
    : '';

  const headerData = options.headerLeft
    ? ` x:Data="&amp;L${xmlEscape(options.headerLeft)}"`
    : '';
  const footerParts = [
    options.footerLeft ? `&amp;L${xmlEscape(options.footerLeft)}` : '',
    options.footerRight ? `&amp;R${xmlEscape(options.footerRight)}` : '',
  ]
    .filter(Boolean)
    .join('');
  const footerData = footerParts ? ` x:Data="${footerParts}"` : '';

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Author>BalanceFlow</Author>
  <Created>${new Date().toISOString()}</Created>
 </DocumentProperties>
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#111827"/>
  </Style>
  <Style ss:ID="Title">
   <Font ss:FontName="Calibri" ss:Size="16" ss:Bold="1" ss:Color="#111827"/>
  </Style>
  <Style ss:ID="Subtitle">
   <Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#374151"/>
  </Style>
  <Style ss:ID="Muted">
   <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#4B5563"/>
  </Style>
  <Style ss:ID="Section">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#6B7280"/>
  </Style>
  <Style ss:ID="Label">
   <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#4B5563"/>
  </Style>
  <Style ss:ID="ColHeader">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1F2937"/>
   </Borders>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#374151" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="ColHeaderNum">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:WrapText="1"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1F2937"/>
   </Borders>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#374151" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="Date">
   <NumberFormat ss:Format="DD/MM/YYYY"/>
  </Style>
  <Style ss:ID="Int">
   <Alignment ss:Horizontal="Right"/>
   <NumberFormat ss:Format="0"/>
  </Style>
  <Style ss:ID="Money">
   <Alignment ss:Horizontal="Right"/>
   <NumberFormat ss:Format="#,##0.00;[Red]-#,##0.00"/>
  </Style>
  <Style ss:ID="MoneyBold">
   <Alignment ss:Horizontal="Right"/>
   <Font ss:Bold="1"/>
   <NumberFormat ss:Format="#,##0.00;[Red]-#,##0.00"/>
   <Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/>
   </Borders>
  </Style>
  <Style ss:ID="TotalLabel">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/>
   </Borders>
  </Style>
  <Style ss:ID="TotalEmpty">
   <Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/>
   </Borders>
  </Style>
  <Style ss:ID="Footer">
   <Font ss:FontName="Calibri" ss:Size="9" ss:Color="#6B7280"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="${xmlEscape(sheetName(options.sheetName))}">
  <Table ss:DefaultRowHeight="16">${columns}${rows}
  </Table>
  ${autoFilter}
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <PageSetup>
    <Layout x:Orientation="Landscape"/>
    <Header x:Margin="0.25"${headerData}/>
    <Footer x:Margin="0.25"${footerData}/>
    <PageMargins x:Bottom="0.5" x:Left="0.4" x:Right="0.4" x:Top="0.5"/>
   </PageSetup>
   <Print>
    <ValidPrinterInfo/>
    <PaperSizeIndex>9</PaperSizeIndex>
    <FitWidth>1</FitWidth>
    <FitHeight>0</FitHeight>
   </Print>
   ${freeze}
   <ProtectObjects>False</ProtectObjects>
   <ProtectScenarios>False</ProtectScenarios>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;

  const blob = new Blob([`\uFEFF${xml}`], {
    type: 'application/vnd.ms-excel;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename.toLowerCase().endsWith('.xls')
    ? filename
    : `${filename}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
